import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { BaseServices } from '../../kuba.services';
import {
  FoodSafetyZoneListKind,
  foodSafetyZoneListKind
} from '../foodsafety-tabs';
import { FoodSafetyList } from '../models/foodsafety-list';
import { FoodSafetyServices } from './foodsafety.services';

const CACHE_KEY = 'foodSafetyNavigation';

/**
 * The Food Safety zone list, kept in session storage for the hover
 * sub-navigation. The zone list page stores the list it shows; before the first
 * visit, the first hover fetches it once.
 */
@Injectable({ providedIn: 'root' })
export class FoodSafetyNavigationService {
  private readonly foodSafetyServices = inject(FoodSafetyServices);
  private pending?: Observable<FoodSafetyList[]>;

  /** The zones the current user's zone list shows. */
  zones(): Observable<FoodSafetyList[]> {
    const kind = this.listKind();
    const cached = this.read(kind);
    if (cached) {
      return of(cached);
    }
    if (!this.pending) {
      const request: Observable<any> =
        kind === 'own'
          ? this.foodSafetyServices.getFoodByUser(BaseServices.UserId)
          : this.foodSafetyServices.getAllByBusinessId(BaseServices.BusinessId);
      this.pending = request.pipe(
        map((result: any) => (Array.isArray(result) ? result : [])),
        tap(list => this.store(kind, list)),
        finalize(() => (this.pending = undefined)),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return this.pending;
  }

  /**
   * Replaces the stored list.
   * @param kind Which list the zones are.
   * @param zones The zones that list shows.
   */
  store(kind: FoodSafetyZoneListKind, zones: FoodSafetyList[] | null | undefined): void {
    try {
      sessionStorage.setItem(this.key(kind), JSON.stringify(zones ?? []));
    } catch {
      // Storage full or unavailable: the next hover fetches the list again.
    }
  }

  /** Drops the stored lists, so the next hover fetches them again. */
  invalidate(): void {
    sessionStorage.removeItem(this.key('all'));
    sessionStorage.removeItem(this.key('own'));
  }

  private listKind(): FoodSafetyZoneListKind {
    return foodSafetyZoneListKind(BaseServices.roleId, rightId =>
      BaseServices.checkUserRights(rightId, BaseServices.UserRole)
    );
  }

  private read(kind: FoodSafetyZoneListKind): FoodSafetyList[] | null {
    try {
      const zones = JSON.parse(sessionStorage.getItem(this.key(kind)) ?? 'null');
      return Array.isArray(zones) ? zones : null;
    } catch {
      return null;
    }
  }

  /** In R mode the business is the entered one. */
  private key(kind: FoodSafetyZoneListKind): string {
    return `${CACHE_KEY}:${kind}:${BaseServices.BusinessId}:${BaseServices.UserId}`;
  }
}
